import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal, ViewChildren } from '@angular/core';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { FormsModule } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { MachineService } from '../../services/machine.service';
import { MachineDashboardDto, MachineDto } from 'src/app/shared/dto/machine.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { Subject, takeUntil } from 'rxjs';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { MachineStatesEnum } from 'src/app/shared/enums/machine-states.enum';
import { CdTimerModule } from 'angular-cd-timer';
import { ModalProductionProcessessSelectorComponent } from '../../components/modals/modal-production-processess-selector/modal-production-processess-selector.component';
import { ProductionOrderDto } from 'src/app/shared/dto/production-order.dto';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import _ from 'lodash';
import { ProductionOrderProcessesService } from '../../services/production-order-processes.service';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { ConfigurationDto } from 'src/app/shared/dto/configuration.dto';
import { SensorDto } from 'src/app/shared/dto/sensor.dto';
import { ConfigurationTypesEnum } from 'src/app/shared/enums/configuration-types.enum';
import { ConfigurationService } from '../../services/configuration.service';
import { SensorsService } from '../../services/sensors.service';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { SensorCardComponent } from '../../components/sensor-card/sensor-card.component';
import { StatusEnum } from 'src/app/shared/enums/production-order-state-ids.enum';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { MachineMeasurementsEnum } from 'src/app/shared/enums/machine-measurements.enum';
import { MachineMeasurementsValueDto } from 'src/app/shared/dto/machine-measurements-value.dto';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';

@Component({
    selector: 'app-machines-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        NzInputGroupComponent,
        NzInputGroupWhitSuffixOrPrefixDirective,
        NzInputDirective,
        NzIconDirective,
        FormsModule,
        CdTimerModule,
        NzSelectComponent,
        NzOptionComponent,
        NzProgressModule,
        NzToolTipModule,
        SensorCardComponent,
        NzCardComponent,
        LoaderComponent
    ],
    templateUrl: './machines-dashboard.component.html',
    styleUrl: './machines-dashboard.component.scss'
})
export class MachinesDashboardComponent implements OnInit, OnDestroy {
    @ViewChildren('timer') timers: any;

    machinesDashboard: MachineDashboardDto[] = [];
    machinesDashboardFiltered: MachineDashboardDto[] = [];
    floors: string[] = ['Planta 1', 'Planta 2', 'Planta 3', 'Planta 4', 'Planta 5'];
    zones: string[] = ['Zona 1', 'Zona 2', 'Zona 3', 'Zona 4', 'Zona 5'];
    lines: string[] = ['Linea 1', 'Linea 2', 'Linea 3', 'Linea 4', 'Linea 5'];
    StatusEnum = StatusEnum;
    machineStatesEnum = MachineStatesEnum;

    isLoading: boolean = false;
    filterText: string = '';
    filterStatus: string = 'ALL';
    currentUser: UserDto = new UserDto();
    idColumnValueToShow: string = 'id';

    sensorsValuesPerMachine: Map<number, SensorDto | undefined> = new Map();

    private sensorConfigKey: string = 'productionOrdersProcessesDetail.sensors';
    private unsubscribe$ = new Subject<void>();
    private readonly router = inject(Router);
    private readonly message = inject(NzMessageService);
    private readonly modalService = inject(NzModalService);
    readonly machineService = inject(MachineService);
    readonly userLoggedService = inject(UserLoggedService);
    private readonly productionOrderProcessesService = inject(ProductionOrderProcessesService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly sensorsService = inject(SensorsService);

    async ngOnInit() {
        try {
            this.isLoading = true;

            this.currentUser = this.userLoggedService.userLogged;
            await this.sensorsService.setupWebSocket();
            await this.machineService.setupWebSocketDashboard();

            this.idColumnValueToShow = (await this.configurationService.getConfigurationByKey('order.idColumnValue')).value.toString() ?? 'id';

            this.machineService.machinesDashboard$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: (machines: MachineDashboardDto[]) => {
                        this.machinesDashboard = machines;
                        this.machinesDashboardFiltered = _.cloneDeep(this.machinesDashboard);

                        for (const machine of this.machinesDashboard) {
                            const firstShowInScreenSensor = this.getFirstShowInScreenSensor(machine);
                            if (firstShowInScreenSensor) {
                                firstShowInScreenSensor.component = 'DASHBOARD';

                                this.sensorsValuesPerMachine.set(machine.id, firstShowInScreenSensor);
                            }

                        }

                        this.filterMachines();
                    },
                    error: () => {
                        this.message.error('Error cargando máquinas');
                    }
                });

            this.filterText = sessionStorage.getItem('machines-dashboard-lastFilter') ?? '';
            this.filterMachines();

            const useSensorConfig: ConfigurationDto = await this.configurationService.getConfigurationByKey(this.sensorConfigKey);

            if (useSensorConfig.type === ConfigurationTypesEnum.BOOLEAN && JSON.parse(useSensorConfig.value as string)) {
                this.sensorsService.machineSensors$
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe({
                        next: (sensor: SensorDto) => {
                            this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.RUNNING).forEach(async (machine: MachineDashboardDto) => {
                                const orderInProcessIndex = this.hasMachineOrderInProgress(machine.productionOrderProcesses);
                                if (orderInProcessIndex !== -1) {
                                    const process = machine.productionOrderProcesses[orderInProcessIndex];
                                    const shownSensor = this.sensorsValuesPerMachine.get(machine.productionOrderProcesses[orderInProcessIndex].machineId!);

                                    const sensorIndex = machine.sensors?.findIndex((s: SensorDto) => s.id === sensor.id);
                                    if (sensor != undefined && machine.productionOrderProcesses[orderInProcessIndex].id === +sensor.orderProcessId
                                        && machine.productionOrderProcesses[orderInProcessIndex].machineId === +sensor.machineId
                                        && shownSensor
                                    ) {
                                        switch (sensorIndex) {
                                            case 2:
                                                if (machine.machineSignals && machine.sensors && machine.sensors[2]) {
                                                    machine.machineSignals.velocity.set(sensor.value ?? 0);
                                                    machine.sensors[2].value = sensor.value ?? 0;
                                                    shownSensor.value = sensor.value;
                                                }
                                                break
                                            case 5:
                                                if (machine.machineSignals && machine.machineSignals.quantityProduced && machine.sensors && machine.sensors[5]) {
                                                    const productionOrderProcessInProgressIndex = this.hasMachineOrderInProgress(machine.productionOrderProcesses);
                                                    const sensorValues = machine.productionOrderProcesses[productionOrderProcessInProgressIndex].sensorValues;
                                                    let prevValue = 0;
                                                    if(sensorValues && sensorValues[5]){
                                                        prevValue = sensorValues[5].value ?? 0;
                                                    }
                                                    const actualValue = sensor.value ?? 0;
                                                    const multiplier = machine.machineSignals?.multiplier() || 1;
                                                    machine.machineSignals.quantityProduced.set((prevValue + actualValue) * multiplier);
                                                }
                                                break
                                        }
                                    }
                                }
                            });
                        }
                    });
            }
            this.isLoading = false;
        } catch (e: any) {
            this.message.error(`${e.message}`);
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.sensorsService.disconnectWebSocket();
        this.machineService.disconnectWebSocketDashboard();
    }

    changeFilterStatus(status: string) {
        this.isLoading = true;
        this.filterStatus = status;
        this.filterMachines();
        this.isLoading = false;
    }

    clearFilter(): void {
        this.isLoading = true;
        this.filterText = '';
        sessionStorage.setItem('machines-dashboard-lastFilter', '');
        this.filterMachines();
        this.isLoading = false;
    }

    filterMachines(): void {
        this.isLoading = true;
        this.machinesDashboardFiltered = this.machinesDashboard.filter((machine: MachineDashboardDto) =>
            machine.name.toLowerCase().includes(this.filterText.toLowerCase()) ||
            machine.productionOrderProcesses.some((process: ProductionOrderProcessDto) => process.description.toLowerCase().includes(this.filterText.toLowerCase())) ||
            machine.productionOrderProcesses.some((process: ProductionOrderProcessDto) => {
                return process.finalProducts!.some((element: any) => {
                    return element.finalProduct.article!.name.toLowerCase().includes(this.filterText.toLowerCase());
                });
            })
        );

        switch (this.filterStatus) {
            case MachineStatesEnum.AVAILABLE:
                this.machinesDashboardFiltered = this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.AVAILABLE);
                break;

            case MachineStatesEnum.MAINTENANCE:
                this.machinesDashboardFiltered = this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.MAINTENANCE);
                break;

            case MachineStatesEnum.RUNNING:
                this.machinesDashboardFiltered = this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.RUNNING);
                break;

            default:
                break;
        }

        for (let machine of this.machinesDashboardFiltered) {
            const inProgressIndex = this.hasMachineOrderInProgress(machine.productionOrderProcesses);
            const inProgressProcess = machine.productionOrderProcesses[inProgressIndex];
            const machineSignals = this.initMachineSignals(inProgressProcess);
            machine.machineSignals = machineSignals;

            if (inProgressProcess) {
                const measurementValue = inProgressProcess.machine?.machineMeasurementsValues?.find(measurementValue =>
                    measurementValue.machineMeasurement?.measurement === MachineMeasurementsEnum.MULTIPLIER
                    && measurementValue.articleId === this.getFinalProductArticleId(inProgressProcess)
                )

                machine.machineSignals.multiplier.set(Number(measurementValue?.value) ?? 1);
            }
        }

        sessionStorage.setItem('machines-dashboard-lastFilter', this.filterText);
        this.isLoading = false;
        this.setTimer();
    }

    hasMachineOrdersAssigned(machine: MachineDashboardDto) {
        return machine.productionOrderProcesses!.length > 0;
    }

    hasMachineOrderInProgress(productionOrderProcesses: ProductionOrderProcessDto[]): number {
        return productionOrderProcesses.findIndex((process: ProductionOrderProcessDto) => process.status!.id == this.StatusEnum.IN_PROGRESS);
    }

    hasMachineShowInScreenSensor(machine: MachineDto): number {
        return machine.sensors?.findIndex((sensor: SensorDto) => sensor.showInScreen === true) ?? -1;
    }

    getFirstShowInScreenSensor(machine: MachineDto): SensorDto | undefined {
        return machine.sensors?.find((sensor: SensorDto) => sensor.showInScreen === true) ?? undefined;
    }

    countAwaitingProcessess(productionOrderProcesses: ProductionOrderProcessDto[]): number {
        return productionOrderProcesses.filter((process: ProductionOrderProcessDto) => process.status!.id == this.StatusEnum.AWAITING).length;
    }

    countPausedProcessess(productionOrderProcesses: ProductionOrderProcessDto[]): number {
        return productionOrderProcesses.filter((process: ProductionOrderProcessDto) => process.status!.id == this.StatusEnum.PAUSED).length;
    }

    calculateProgress(process: any, machineSignals: any): number {
        if (!process.finalProducts) {
            return 0;
        }

        const quantityToProduce = this.getQuantityToProduce(process);

        let quantityProduced = machineSignals?.quantityProduced?.() || 0;

        return (quantityProduced === 0 || quantityToProduce === 0) ? 0 : Math.round((quantityProduced / quantityToProduce) * 100);
    }

    getOrderTitle(order: ProductionOrderDto, process: ProductionOrderProcessDto): string {
        let idValueToShow = '-';

        if (this.idColumnValueToShow === 'id') {
            idValueToShow = order.id.toString();
        } else if (this.idColumnValueToShow === 'externalId') {
            idValueToShow = order.externalId?.toString() ?? '';
        } else if (this.idColumnValueToShow === 'finalProduct') {
            idValueToShow = this.getIdColumnValue(process);
        }

        const processDesc = process?.description ?? '';

        return `Orden ${idValueToShow} | ${processDesc} `;
    }

    getQuantityToProduce(process: any): number {
        if (!process.finalProducts) {
            return 0;
        }

        const quantityToProduce = process.finalProducts.reduce((total: any, finalProduct: { finalProduct: { quantityToProduce: any; }; }) => {
            return total + (finalProduct.finalProduct.quantityToProduce || 0);
        }, 0);

        return quantityToProduce;
    }


    getFinalProductArticleId(process: any): number | undefined {
        return process?.finalProducts?.[0]?.finalProduct?.article?.id;
    }

    initMachineSignals(process: any) {
        const multiplier = signal(1);
        if (process && process.machine && process.machine.machineMeasurementsValues) {
            process.machine?.machineMeasurementsValues?.forEach((measurementValue: MachineMeasurementsValueDto) => {
                if (measurementValue.machineMeasurement?.measurement === MachineMeasurementsEnum.MULTIPLIER
                    && measurementValue.articleId === this.getFinalProductArticleId(process)
                ) {
                    multiplier.set(Number(measurementValue.value) ?? 1);
                }
            })
        }

        const velocity = signal(0);
        const quantityProduced = signal(0);
        const quantityToProduce = signal(0);

        if (process?.finalProducts) {
            let totalQuantityToProduce = 0;

            for (const fp of process.finalProducts) {
                if (fp.finalProduct?.quantityToProduce) {
                    totalQuantityToProduce += fp.finalProduct.quantityToProduce;
                }
            }

            quantityToProduce.set(totalQuantityToProduce);
        }

        const estimatedTime = computed(() => {
            const remainingQuantity = quantityToProduce() - quantityProduced();
            const m = multiplier() || 1;
            const v = velocity();

            if (remainingQuantity > 0 && !isNaN(remainingQuantity) && velocity() > 0) {
                return (this.formatEstimatedTime(remainingQuantity / v / m)); // * 60 min;
            }

            return 0;
        });

        return {
            multiplier,
            quantityToProduce,
            velocity,
            quantityProduced,
            estimatedTime,
        };
    }

    private getIdColumnValue(process: any): string {
        return process &&
            process?.finalProducts?.[0]?.finalProduct.articleBatch?.batchNumber || '';
    }

    private async setTimer() {
        try {
            this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.RUNNING).forEach(async (machine: MachineDashboardDto) => {
                const currentTimeMilliseconds: number = await this.productionOrderProcessesService.getTimerByProductionOrderProcessId(machine.productionOrderProcesses![this.hasMachineOrderInProgress(machine.productionOrderProcesses)!].id!);
                const positionMachine = this.machinesDashboardFiltered.filter((machine: MachineDashboardDto) => machine.state === MachineStatesEnum.RUNNING).findIndex((machine2: MachineDashboardDto) => machine.id === machine2.id);

                if (currentTimeMilliseconds > 0) {
                    this.timers.toArray()[positionMachine].startTime = Math.max(0, Math.round((currentTimeMilliseconds / 1000 + Number.EPSILON) * 100) / 100 - 1);
                } else {
                    this.timers.toArray()[positionMachine].startTime = 0;
                }

                this.timers.toArray()[positionMachine].start();
            });
        } catch (error: any) {
            this.message.error('Error estableciendo timer:' + error.message);
        }
    }

    async navigateToProductionOrder(machine: MachineDashboardDto): Promise<void> {
        const inProgressIndex = this.hasMachineOrderInProgress(machine.productionOrderProcesses);

        if (inProgressIndex !== -1) {
            const process = machine.productionOrderProcesses[inProgressIndex];

            if (await this.showOpenInNewTabDialog()) {
                this.router.navigate([]).then(() => window.open(`/production-orders/${process.productionOrder!.id}/process/${process.id}`, '_blank'));
            } else {
                this.router.navigate([`/production-orders/${process.productionOrder!.id}/process/${process.id}`]);
            }
        } else {
            this.openMachineProcessesModal(machine);
        }
    }

    async showOpenInNewTabDialog(): Promise<boolean> {
        return new Promise((resolve) => {
            this.modalService.confirm({
                nzTitle: '<i>¿Desea abrir el proceso de la orden de producción seleccionada en otra pestaña del navegador?</i>',
                nzClosable: false,
                nzOkText: 'Nueva pestaña',
                nzOnOk: () => { resolve(true); },
                nzCancelText: 'Permanecer en esta',
                nzOnCancel: () => { resolve(false); }
            });
        });
    }

    formatEstimatedTime(minutes: number): string {
        const totalSeconds = Math.round(minutes * 60);
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        if (hours > 0) {
            return `${hours}h ${mins}min`;
        } else {
            return `${mins}min ${secs}s`;
        }
    }

    private openMachineProcessesModal(machine: MachineDashboardDto) {
        const modal = this.modalService.create({
            nzContent: ModalProductionProcessessSelectorComponent,
            nzStyle: { width: '70%', top: '20%' },
            nzBodyStyle: { height: '30vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Seleccione a que proceso de la máquina ${machine.name} desea navegar`,
                productionOrderProcesses: machine.productionOrderProcesses
            }
        });
    }
}
